// Finds the earlier email a reply quotes, so the reader can fold it away and snippets skip it.

/** "On Mon, Paul wrote:" in the languages mail apps write it in. */
const ATTRIBUTION =
  /^(on\b.{0,300}\bwrote|le\b.{0,300}\ba [ée]crit|am\b.{0,300}\bschrieb.{0,120}|op\b.{0,300}\bschreef.{0,120}|el\b.{0,300}\bescribi[óo]|il\b.{0,300}\bha scritto|em\b.{0,300}\bescreveu|den\b.{0,300}\bskrev.{0,120})\s*:\s*$/i;

/** Outlook's line above the email it answers. */
const SEPARATOR =
  /^\s*-{2,}\s*(original message|message d'origine|urspr[üu]ngliche nachricht|oorspronkelijk bericht|mensaje original|messaggio originale)\s*-{2,}\s*$/i;

/** Outlook's header block: "From: …" with "Sent: …" or "Date: …" just below. */
const HEADER_FROM = /^\s*\**(from|de|von|van|da)\s*:\**\s+\S/i;
const HEADER_DATE =
  /^\s*\**(sent|date|envoy[ée]|gesendet|verzonden|enviado|inviato|datum)\s*:\**\s+\S/i;

const depth = (line: string) => /^(\s*>)*/.exec(line)?.[0].replace(/[^>]/g, "").length ?? 0;
const unquoted = (line: string) => line.replace(/^(\s*>)+\s?/, "");
const blank = (line: string) => line.replace(/^(\s*>)+/, "").trim() === "";

/** Lines from `index` that open quoted history, and whether everything after it belongs to it. */
const opening = (lines: readonly string[], index: number): { lines: number; rest: boolean } => {
  const line = lines[index] ?? "";
  const text = unquoted(line).trim();
  if (ATTRIBUTION.test(text)) return { lines: 1, rest: false };
  // "On …, Paul <paul@example.com>" wrapped onto a second line ending in "wrote:".
  const next = unquoted(lines[index + 1] ?? "").trim();
  if (/^on\b/i.test(text) && next && ATTRIBUTION.test(`${text} ${next}`))
    return { lines: 2, rest: false };
  if (SEPARATOR.test(text)) return { lines: 1, rest: true };
  if (
    HEADER_FROM.test(text) &&
    lines.slice(index + 1, index + 4).some((later) => HEADER_DATE.test(unquoted(later)))
  )
    return { lines: 1, rest: true };
  if (depth(line) > 0) return { lines: 1, rest: false };
  return { lines: 0, rest: false };
};

export type QuoteSplit = {
  /** The new words above the quoted email. */
  before: string;
  /** The quoted email, with its "On … wrote:" line; empty when there is none. */
  quoted: string;
  /** New words below it, for people who answer under the quote. */
  after: string;
};

/**
 * Splits a plain-text email around the earlier email it quotes. Only a single quoted block is
 * folded: answers written between quoted lines stay as they are, since the quote is their context.
 */
export const splitQuote = (text: string): QuoteSplit => {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const none = { before: text, quoted: "", after: "" };
  for (let start = 0; start < lines.length; start++) {
    const open = opening(lines, start);
    if (open.lines === 0) continue;
    let end = lines.length;
    if (!open.rest) {
      end = start + open.lines;
      while (end < lines.length && (depth(lines[end] ?? "") > 0 || blank(lines[end] ?? ""))) end++;
      // Blank lines after the quote belong to what follows.
      while (end > start + open.lines && blank(lines[end - 1] ?? "")) end--;
      // A bare "On … wrote:" with nothing quoted under it is just text.
      if (end === start + open.lines && depth(lines[start] ?? "") === 0) continue;
      // More quoted lines further down: an interleaved answer, so nothing is folded.
      if (lines.slice(end).some((line) => depth(line) > 0)) return none;
    }
    const before = lines.slice(0, start).join("\n");
    const after = lines.slice(end).join("\n");
    // A quote with no new words around it (say, a bare forward) is the whole message.
    if (!before.trim() && !after.trim()) return none;
    return { before, quoted: lines.slice(start, end).join("\n"), after };
  }
  return none;
};

/** The text without the quoted email, for snippets and previews. */
export const withoutQuote = (text: string): string => {
  const split = splitQuote(text);
  if (!split.quoted) return text;
  return [split.before.trimEnd(), split.after.trimStart()].filter(Boolean).join("\n");
};

/** True for a line that opens quoted history, such as "On Monday, Paul wrote:". */
export const isAttribution = (line: string) => {
  const text = line.trim();
  return ATTRIBUTION.test(text) || SEPARATOR.test(text);
};

/** Quoted lines as levels, for drawing nested quotes: [depth, text without the "> "]. */
export const quoteLevels = (quoted: string): Array<[number, string]> =>
  quoted.split("\n").map((line) => [depth(line), unquoted(line)]);

const SNIPPET_SUBJECT = /^\s*\**subject:\**\s*/i;
const SNIPPET_ATTRIBUTION =
  /\s+(on|le|am|op|el|il|em|den)\s.{0,300}?\b(wrote|a [ée]crit|schrieb|schreef|escribi[óo]|ha scritto|escreveu|skrev)\b[^:]{0,120}:.*$/i;

/**
 * A stored snippet as a preview: without a "Subject: …" line the body repeats, or the
 * "On … wrote:" line of the quoted email (snippets saved before quotes were left out).
 */
export const cleanSnippet = (snippet: string, subject: string): string => {
  let text = snippet.replace(SNIPPET_ATTRIBUTION, "");
  if (SNIPPET_SUBJECT.test(text)) {
    const rest = text.replace(SNIPPET_SUBJECT, "");
    const bare = subject.replace(/^\s*((re|fwd?|aw|wg|sv|tr)\s*:\s*)+/i, "").trim();
    if (bare && rest.toLowerCase().startsWith(bare.toLowerCase())) text = rest.slice(bare.length);
  }
  return text.trim() || snippet;
};
